import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Badge, Button, Spinner, Form } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { bugsAPI, commentsAPI } from '../services/api';
import { format } from 'date-fns';
import { useAuth } from '../utils/AuthContext';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

function BugDetails() {
  const { id } = useParams();
  const [bug, setBug] = useState(null);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchBugDetails();
    fetchComments();
    fetchAttachments();
  }, [id]);

  const fetchBugDetails = async () => {
    try {
      setLoading(true);
      const response = await bugsAPI.getBug(id);
      setBug(response.data);
    } catch (error) {
      toast.error('Failed to load bug details');
      console.error(error);
      navigate('/bugs');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await commentsAPI.getBugComments(id);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to load comments', error);
    }
  };

  const fetchAttachments = async () => {
    try {
      const response = await bugsAPI.getBugAttachments(id);
      console.log('Fetched attachments:', response.data);
      setAttachments(response.data);
    } catch (error) {
      console.error('Failed to load attachments', error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }

    try {
      setSubmittingComment(true);
      await commentsAPI.createComment({
        bug_id: parseInt(id),
        content: newComment
      });
      setNewComment('');
      toast.success('Comment added successfully');
      fetchComments();
    } catch (error) {
      toast.error('Failed to add comment');
      console.error(error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setUpdatingStatus(true);
      await bugsAPI.updateBug(id, {
        ...bug,
        status: newStatus,
        project_id: bug.project_id,
        assigned_to: bug.assigned_to
      });
      setBug({ ...bug, status: newStatus });
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error('Failed to update status');
      console.error(error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'Open': 'danger',
      'In Progress': 'primary',
      'Resolved': 'success',
      'Closed': 'secondary',
      'Re-opened': 'warning',
      'Not a Bug': 'info',
      'Cannot Reproduce': 'secondary',
      'Pending QA': 'warning'
    };
    return <Badge bg={statusColors[status] || 'secondary'} style={{ fontSize: '14px', padding: '6px 12px' }}>{status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const priorityColors = {
      'Critical': 'danger',
      'High': 'warning',
      'Medium': 'primary',
      'Low': 'secondary'
    };
    const priorityEmojis = {
      'Critical': '🔴',
      'High': '🟠',
      'Medium': '🟡',
      'Low': '🟢'
    };
    return (
      <Badge bg={priorityColors[priority] || 'secondary'} style={{ fontSize: '14px', padding: '6px 12px' }}>
        {priorityEmojis[priority]} {priority}
      </Badge>
    );
  };

  const getBugTypeBadge = (bugType) => {
    const typeColors = {
      'Bug': 'danger',
      'Feature': 'success',
      'Enhancement': 'info',
      'Ticket': 'warning'
    };
    return <Badge bg={typeColors[bugType] || 'secondary'} style={{ fontSize: '14px', padding: '6px 12px' }}>{bugType}</Badge>;
  };

  if (loading) {
    return (
      <Container fluid style={{ padding: '2rem 3rem' }}>
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      </Container>
    );
  }

  if (!bug) {
    return (
      <Container fluid style={{ padding: '2rem 3rem' }}>
        <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
          <Card.Body style={{ padding: '3rem', textAlign: 'center' }}>
            <h3 style={{ color: '#666' }}>Bug not found</h3>
            <Button variant="primary" onClick={() => navigate('/bugs')} className="mt-3">
              Back to Bugs
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const canModify = user?.role === 'Admin' || user?.role === 'QA';

  return (
    <Container fluid style={{ padding: '2rem 3rem', maxWidth: '1400px' }}>
      {/* Bug Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-3">
          <Badge
            bg="primary"
            style={{ borderRadius: '6px', padding: '8px 14px', fontSize: '16px', fontWeight: '600' }}
          >
            {bug.bug_number}
          </Badge>
          <h2 className="fw-bold mb-0" style={{ fontSize: '28px', color: '#1a1a1a' }}>
            {bug.title}
          </h2>
        </div>
        <div className="d-flex gap-2">
          {canModify && (
            <Button
              variant="outline-primary"
              onClick={() => navigate(`/bugs/${bug.id}/edit`)}
              style={{
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: '500',
                fontSize: '14px'
              }}
            >
              Edit
            </Button>
          )}
          <Button
            variant="outline-secondary"
            onClick={() => navigate('/bugs')}
            style={{
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '500',
              fontSize: '14px'
            }}
          >
            Back to Bugs
          </Button>
        </div>
      </div>

      <Row className="g-3">
        {/* Left Column - Bug Details */}
        <Col lg={8}>
          {/* Status and Priority */}
          <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px' }}>
            <Card.Body style={{ padding: '1.25rem' }}>
              <div className="d-flex gap-3 align-items-center">
                <div>
                  <small style={{ fontSize: '12px', color: '#999', display: 'block', marginBottom: '4px' }}>Status</small>
                  {getStatusBadge(bug.status)}
                </div>
                <div>
                  <small style={{ fontSize: '12px', color: '#999', display: 'block', marginBottom: '4px' }}>Priority</small>
                  {getPriorityBadge(bug.priority)}
                </div>
                <div>
                  <small style={{ fontSize: '12px', color: '#999', display: 'block', marginBottom: '4px' }}>Type</small>
                  {getBugTypeBadge(bug.bug_type)}
                </div>
                <div>
                  <small style={{ fontSize: '12px', color: '#999', display: 'block', marginBottom: '4px' }}>Project</small>
                  <Badge bg="secondary" style={{ fontSize: '13px', padding: '6px 12px' }}>{bug.project_name}</Badge>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Description */}
          <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px' }}>
            <Card.Body style={{ padding: '1.5rem' }}>
              <h5 className="fw-bold mb-3" style={{ fontSize: '16px', color: '#1a1a1a' }}>Description</h5>
              <div
                className="ql-editor"
                style={{ padding: 0 }}
                dangerouslySetInnerHTML={{ __html: bug.description }}
              />
            </Card.Body>
          </Card>

          {/* Steps to Reproduce */}
          {bug.steps_to_reproduce && (
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px' }}>
              <Card.Body style={{ padding: '1.5rem' }}>
                <h5 className="fw-bold mb-3" style={{ fontSize: '16px', color: '#1a1a1a' }}>Steps to Reproduce</h5>
                <div
                  className="ql-editor"
                  style={{ padding: 0 }}
                  dangerouslySetInnerHTML={{ __html: bug.steps_to_reproduce }}
                />
              </Card.Body>
            </Card>
          )}

          {/* Expected & Actual Results */}
          {(bug.expected_result || bug.actual_result) && (
            <Row className="g-3 mb-3">
              {bug.expected_result && (
                <Col md={6}>
                  <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
                    <Card.Body style={{ padding: '1.5rem' }}>
                      <h5 className="fw-bold mb-3" style={{ fontSize: '16px', color: '#1a1a1a' }}>Expected Result</h5>
                      <div
                        className="ql-editor"
                        style={{ padding: 0 }}
                        dangerouslySetInnerHTML={{ __html: bug.expected_result }}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              )}
              {bug.actual_result && (
                <Col md={6}>
                  <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
                    <Card.Body style={{ padding: '1.5rem' }}>
                      <h5 className="fw-bold mb-3" style={{ fontSize: '16px', color: '#1a1a1a' }}>Actual Result</h5>
                      <div
                        className="ql-editor"
                        style={{ padding: 0 }}
                        dangerouslySetInnerHTML={{ __html: bug.actual_result }}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              )}
            </Row>
          )}

          {/* URL */}
          {bug.url && (
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px' }}>
              <Card.Body style={{ padding: '1.5rem' }}>
                <h5 className="fw-bold mb-2" style={{ fontSize: '16px', color: '#1a1a1a' }}>URL</h5>
                <a href={bug.url} target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', fontSize: '14px' }}>
                  {bug.url}
                </a>
              </Card.Body>
            </Card>
          )}

          {/* Attachments */}
          {attachments.length > 0 && (
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px' }}>
              <Card.Body style={{ padding: '1.5rem' }}>
                <h5 className="fw-bold mb-3" style={{ fontSize: '16px', color: '#1a1a1a' }}>
                  Attachments ({attachments.length})
                </h5>
                <Row className="g-3">
                  {attachments.map((attachment) => {
                    console.log('Rendering attachment:', attachment);
                    return (
                    <Col key={attachment.id} xs={12} sm={6} md={4}>
                      <Card style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                        {attachment.file_type === 'image' || attachment.mime_type?.startsWith('image/') ? (
                          <a href={attachment.s3_url} target="_blank" rel="noopener noreferrer">
                            <Card.Img
                              variant="top"
                              src={attachment.s3_url}
                              style={{ height: '200px', objectFit: 'cover', cursor: 'pointer' }}
                              alt={attachment.filename}
                            />
                          </a>
                        ) : attachment.file_type === 'video' || attachment.mime_type?.startsWith('video/') ? (
                          <video
                            src={attachment.s3_url}
                            controls
                            style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                          />
                        ) : (
                          <div
                            style={{
                              height: '200px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f5f5f5'
                            }}
                          >
                            <i className="bi bi-file-earmark" style={{ fontSize: '48px', color: '#999' }}></i>
                          </div>
                        )}
                        <Card.Body style={{ padding: '12px' }}>
                          <p style={{ fontSize: '13px', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {attachment.filename}
                          </p>
                          <small style={{ fontSize: '11px', color: '#999' }}>
                            {format(new Date(attachment.created_at), 'MMM dd, yyyy')}
                          </small>
                          <div className="mt-2">
                            <a
                              href={attachment.s3_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ fontSize: '12px', color: '#0066cc', textDecoration: 'none' }}
                            >
                              <i className="bi bi-download me-1"></i>
                              Download
                            </a>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                  })}
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Comments Section */}
          <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
            <Card.Body style={{ padding: '1.5rem' }}>
              <h5 className="fw-bold mb-3" style={{ fontSize: '16px', color: '#1a1a1a' }}>
                Comments ({comments.length})
              </h5>

              {/* Add Comment Form */}
              <Form onSubmit={handleCommentSubmit} className="mb-4">
                <Form.Group>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    style={{
                      borderRadius: '8px',
                      padding: '12px',
                      fontSize: '14px',
                      border: '1px solid #e0e0e0',
                      marginBottom: '10px'
                    }}
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={submittingComment}
                    style={{
                      borderRadius: '8px',
                      padding: '8px 20px',
                      fontWeight: '600',
                      fontSize: '14px',
                      border: 'none'
                    }}
                  >
                    {submittingComment ? 'Adding...' : 'Add Comment'}
                  </Button>
                </Form.Group>
              </Form>

              {/* Comments List */}
              <div>
                {comments.length > 0 ? (
                  comments.map((comment) => (
                    <div
                      key={comment.id}
                      style={{
                        borderBottom: '1px solid #f0f0f0',
                        paddingBottom: '1rem',
                        marginBottom: '1rem'
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <strong style={{ fontSize: '14px', color: '#333' }}>{comment.author_name}</strong>
                          <small style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>
                            {format(new Date(comment.created_at), 'MMM dd, yyyy HH:mm')}
                          </small>
                        </div>
                      </div>
                      <p style={{ fontSize: '14px', color: '#555', margin: 0, whiteSpace: 'pre-wrap' }}>
                        {comment.content}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-muted mb-0" style={{ fontSize: '14px' }}>No comments yet</p>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Right Column - Metadata */}
        <Col lg={4}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '10px', position: 'sticky', top: '20px' }}>
            <Card.Body style={{ padding: '1.5rem' }}>
              <h6 className="fw-bold mb-3" style={{ fontSize: '15px', color: '#1a1a1a' }}>Bug Information</h6>

              {/* Status Change */}
              {canModify && (
                <div className="mb-3">
                  <small style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '6px', fontWeight: '600' }}>
                    Change Status
                  </small>
                  <Form.Select
                    value={bug.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={updatingStatus}
                    style={{
                      borderRadius: '8px',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <option value="Open">Open</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Resolved">Resolved</option>
                    <option value="Closed">Closed</option>
                    <option value="Re-opened">Re-opened</option>
                    <option value="Not a Bug">Not a Bug</option>
                    <option value="Cannot Reproduce">Cannot Reproduce</option>
                    <option value="Pending QA">Pending QA</option>
                  </Form.Select>
                </div>
              )}

              <hr style={{ margin: '1rem 0', borderColor: '#e0e0e0' }} />

              {/* Assignee */}
              <div className="mb-3">
                <small style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '6px', fontWeight: '600' }}>
                  Assigned To
                </small>
                <p style={{ fontSize: '14px', color: '#333', margin: 0 }}>
                  {bug.assignee_name || 'Unassigned'}
                </p>
              </div>

              {/* Reporter */}
              <div className="mb-3">
                <small style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '6px', fontWeight: '600' }}>
                  Reporter
                </small>
                <p style={{ fontSize: '14px', color: '#333', margin: 0 }}>
                  {bug.reporter_name}
                </p>
              </div>

              <hr style={{ margin: '1rem 0', borderColor: '#e0e0e0' }} />

              {/* Created Date */}
              <div className="mb-3">
                <small style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '6px', fontWeight: '600' }}>
                  Created
                </small>
                <p style={{ fontSize: '14px', color: '#333', margin: 0 }}>
                  {format(new Date(bug.created_at), 'MMM dd, yyyy HH:mm')}
                </p>
              </div>

              {/* Updated Date */}
              <div className="mb-3">
                <small style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '6px', fontWeight: '600' }}>
                  Last Updated
                </small>
                <p style={{ fontSize: '14px', color: '#333', margin: 0 }}>
                  {format(new Date(bug.updated_at), 'MMM dd, yyyy HH:mm')}
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default BugDetails;
